import { Head, router } from "@inertiajs/react"
import { Download, TrendingUp } from "lucide-react"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Imports",
    href: "/imports",
  },
]

interface ImportsProps {
  latestRates: Array<{
    id: number
    date: string
    rate: number
    from_currency: string
    to_currency: string
  }>
  latestInpc: Array<{
    id: number
    date: string
    value: number
  }>
  ratesCount: number
  inpcCount: number
}

export default function Imports({
  latestRates,
  latestInpc,
  ratesCount,
  inpcCount,
}: ImportsProps) {
  const triggerRates = () => {
    router.post("/imports/trigger_rates", {}, {
      onSuccess: () => {
        // Flash message from controller will show automatically
      },
    })
  }

  const triggerInpc = () => {
    router.post("/imports/trigger_inpc", {}, {
      onSuccess: () => {
        // Flash message from controller will show automatically
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Imports" />

      <div className="flex flex-col gap-5 p-4">
        <h1 className="text-3xl font-bold tracking-tight">Data Imports</h1>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Exchange Rates Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Exchange Rates
                  </CardTitle>
                  <CardDescription>
                    Fetch USD/MXN rates from Banxico
                  </CardDescription>
                </div>
                <Button onClick={triggerRates} size="sm">
                  Fetch Now
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Records:</span>
                  <span className="font-medium">{ratesCount}</span>
                </div>

                {latestRates.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Latest 10 Rates:</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Pair</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {latestRates.map((rate) => (
                          <TableRow key={rate.id}>
                            <TableCell className="font-medium">{rate.date}</TableCell>
                            <TableCell>{Number(rate.rate).toFixed(6)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {rate.from_currency}/{rate.to_currency}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No exchange rate data yet. Click "Fetch Now" to import data.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* INPC Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    INPC Data
                  </CardTitle>
                  <CardDescription>
                    Fetch inflation index from Banxico
                  </CardDescription>
                </div>
                <Button onClick={triggerInpc} size="sm">
                  Fetch Now
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Records:</span>
                  <span className="font-medium">{inpcCount}</span>
                </div>

                {latestInpc.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Latest 10 Values:</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {latestInpc.map((inpc) => (
                          <TableRow key={inpc.id}>
                            <TableCell className="font-medium">{inpc.date}</TableCell>
                            <TableCell>{Number(inpc.value).toFixed(4)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No INPC data yet. Click "Fetch Now" to import data.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
